import React, { useState } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  message,
  Empty,
  Select,
  Dropdown,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  MoreOutlined,
  EditOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import { BulkBarcodePrintModal } from "../products/common";
import {
  useSuppliersQuery,
  usePurchasesQuery,
  useDeletePurchaseMutation,
  useReturnPurchaseMutation,
  usePurchaseItemsRequest,
} from "../../Services";
import { Purchase, PurchaseAction } from "../../Types";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader, tablePagination } from "../../components";
import { PurchaseDrawer, TopBarField } from "./common";
import { useSectionAccess } from "../../Services";

export const PurchasesPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
  const [barcodePrintModalOpen, setBarcodePrintModalOpen] = useState(false);
  const [barcodePrintItems, setBarcodePrintItems] = useState<any[]>([]);
  const fetchPurchaseItems = usePurchaseItemsRequest();

  const { data: suppliers = [], refetch: refetchSuppliers } =
    useSuppliersQuery();

  const {
    data: purchases,
    isLoading,
    refetch,
  } = usePurchasesQuery(
    {
      page,
      limit: PAGE_SIZE,
      search,
      supplier_id: supplierId,
      status: status || undefined,
      payment_status: paymentStatus || undefined,
    },
    {
      placeholderData: (previousData) => previousData,
    },
  );

  const total = purchases?.total || 0;

  const deleteMutation = useDeletePurchaseMutation();
  const returnMutation = useReturnPurchaseMutation();

  const { isAllowed } = useSectionAccess("purchases");

  const openDrawer = (purchase?: Purchase) => {
    setEditingPurchase(purchase || null);
    setDrawerOpen(true);
  };

  const closeDrawer = (shouldRefetch = false) => {
    setDrawerOpen(false);
    setEditingPurchase(null);
    if (shouldRefetch) {
      refetch();
    }
  };

  const nestedItemsColumns = [
    {
      title: "Product",
      key: "product",
      render: (_: any, rec: any) => (
        <div>
          <span className="font-semibold text-gray-800 text-xs">
            {rec.product_title}
          </span>
          <div className="text-[10px] text-gray-400 mt-0.5">
            SKU: {rec.product_sku} | Barcode: {rec.product_barcode}
          </div>
        </div>
      ),
    },
    {
      title: "Stock Option",
      dataIndex: "stock_option",
      key: "stock_option",
      render: (v: string, rec: any) => (
        <Tag
          color={v === "existing" ? "purple" : "blue"}
          className="capitalize text-[10px]"
        >
          {v === "existing"
            ? `Existing Batch (${rec.stock_batch_no})`
            : `New Batch (${rec.batch_no})`}
        </Tag>
      ),
    },
    {
      title: "Cost",
      dataIndex: "cost_price",
      key: "cost_price",
      render: (v: number) => (
        <span className="text-red-500 font-medium text-xs">
          Rs. {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Selling Prices (Retail / Wholesale)",
      key: "selling",
      render: (_: any, rec: any) => (
        <span className="text-[11px] text-gray-500 font-medium">
          Retail:{" "}
          <span className="text-green-600">
            Rs. {rec.retail_price.toFixed(2)}
          </span>{" "}
          / Whole:{" "}
          <span className="text-blue-600">
            Rs. {rec.wholesale_price.toFixed(2)}
          </span>
        </span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (v: number, rec: any) => (
        <Tag color="orange" className="font-bold text-xs">
          {v} {rec.product_unit}
        </Tag>
      ),
    },
    {
      title: "Disc (%)",
      dataIndex: "discount_percent",
      key: "discount_percent",
      render: (v: number) => (
        <span className="text-gray-500 text-xs">{v}%</span>
      ),
    },
    {
      title: "Subtotal",
      dataIndex: "total",
      key: "total",
      render: (v: number) => (
        <span className="font-bold text-gray-700 text-xs">
          Rs. {v.toFixed(2)}
        </span>
      ),
    },
  ];

  const columns = [
    {
      title: "Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <CalendarOutlined className="text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Invoice & PO No",
      key: "invoice_po",
      render: (_: any, rec: Purchase) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-gray-700 text-xs">
            Inv: {rec.invoice_no || "N/A"}
          </span>
          {rec.po_no && (
            <span className="text-[10px] text-gray-400">PO: {rec.po_no}</span>
          )}
        </div>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v: string) => (
        <span className="font-semibold text-gray-700 text-xs">{v}</span>
      ),
    },
    {
      title: "Totals",
      key: "totals",
      render: (_: any, rec: Purchase) => (
        <div className="flex flex-col">
          <span className="text-xs text-gray-400">
            Gross: Rs. {rec.total_amount.toFixed(2)}
          </span>
          {rec.discount_amount > 0 && (
            <span className="text-[10px] text-red-400">
              Disc: -Rs. {rec.discount_amount.toFixed(2)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      render: (v: number) => (
        <span className="font-bold text-indigo-600 text-xs">
          Rs. {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Payment Details",
      key: "payments",
      render: (_: any, rec: Purchase) => (
        <div className="flex flex-col">
          <span className="text-xs text-green-600 font-medium">
            Paid: Rs. {rec.paid_amount.toFixed(2)}
          </span>
          {rec.due_amount > 0 ? (
            <span className="text-[10px] text-red-500 font-bold">
              Due: Rs. {rec.due_amount.toFixed(2)}
            </span>
          ) : (
            <span className="text-[10px] text-gray-300">No Due</span>
          )}
        </div>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (s: string) => {
        const color =
          s === "Paid" ? "success" : s === "Partial" ? "warning" : "error";
        return (
          <Tag color={color} className="text-[10px] uppercase font-bold">
            {s}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => {
        let color = "gold";
        if (s === "Completed") color = "cyan";
        else if (s === "Pending") color = "blue";
        else if (s === "Returned") color = "red";
        return (
          <Tag color={color} className="text-[10px] uppercase font-bold">
            {s}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (_: any, rec: Purchase) => {
        const canEditOrDelete =
          rec.status === "Draft" || rec.status === "Pending";
        const isCompleted = rec.status === "Completed";
        const menuItems = [
          {
            key: "view",
            label: "View Purchase Details",
            icon: <ShoppingCartOutlined />,
            onClick: () => openDrawer(rec),
          },
          {
            key: "print_barcode",
            label: "Print Barcode Labels",
            icon: <PrinterOutlined />,
            onClick: async () => {
              const hideLoading = message.loading(
                "Fetching purchased items...",
                0,
              );
              try {
                const mappedItems = await fetchPurchaseItems.mutateAsync(
                  rec.id,
                );
                if (mappedItems.length === 0) {
                  message.warning("No items found in this purchase to print.");
                } else {
                  setBarcodePrintItems(mappedItems);
                  setBarcodePrintModalOpen(true);
                }
              } catch (err: any) {
                message.error(
                  "An error occurred while loading purchase items.",
                );
              } finally {
                hideLoading();
              }
            },
          },
          ...(canEditOrDelete && isAllowed(PurchaseAction.Edit)
            ? [{
                key: "edit",
                label: "Edit Order",
                icon: <EditOutlined />,
                onClick: () => openDrawer(rec),
              }]
            : []),
          ...(canEditOrDelete && isAllowed(PurchaseAction.Delete)
            ? [{
                key: "delete",
                label: "Delete Order",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: "Delete Purchase Order?",
                    content:
                      "Are you sure you want to permanently delete this purchase order?",
                    okText: "Delete",
                    okButtonProps: { danger: true },
                    cancelText: "Cancel",
                    onOk: async () => {
                      try {
                        const res = await deleteMutation.mutateAsync(rec.id);
                        if (res?.success) {
                          message.success(
                            "Purchase order deleted successfully.",
                          );
                        } else {
                          message.error(
                            res?.message ||
                              "Failed to delete purchase order.",
                          );
                        }
                      } catch (err: any) {
                        message.error(
                          err?.message || "Failed to delete purchase order.",
                        );
                      }
                    },
                  });
                },
              }]
            : []),
          ...(isCompleted
            ? [
                {
                  key: "return",
                  label: "Return Purchase Items",
                  icon: <ReloadOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Return Purchased Items?",
                      content:
                        "Are you sure you want to return this purchase? This will deduct the item quantities from stock and clear any outstanding due balance.",
                      okText: "Return Items",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: async () => {
                        try {
                          const res = await returnMutation.mutateAsync(
                            String(rec.id),
                          );
                          if (res?.success) {
                            message.success("Purchase returned successfully.");
                          } else {
                            message.error(
                              res?.message || "Failed to return purchase.",
                            );
                          }
                        } catch (err: any) {
                          message.error(
                            err?.message || "Failed to return purchase.",
                          );
                        }
                      },
                    });
                  },
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              className="text-gray-500 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Purchase Management"
        subText={`Total purchase records: ${total}`}
        handleButtons={[
          ...(isAllowed(PurchaseAction.Create) ? [{
            title: "Add Purchase",
            icon: PlusOutlined,
            onClick: () => openDrawer(),
          }] : []),
          {
            title: "",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <TopBarField
            suppliers={suppliers}
            setSupplierId={setSupplierId}
            setPage={setPage}
            setStatus={setStatus}
            setPaymentStatus={setPaymentStatus}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={purchases?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "purchases",
          })}
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="px-6 py-4 bg-gray-50/50 rounded-lg border border-gray-100 m-2">
                <h4 className="text-xs font-bold text-indigo-600 mb-3 uppercase tracking-wider">
                  Purchased Product Items
                </h4>
                <Table
                  dataSource={record.items || []}
                  columns={nestedItemsColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  bordered
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No items found."
                      />
                    ),
                  }}
                />
              </div>
            ),
            rowExpandable: (record) => record.items && record.items.length > 0,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No purchases logged yet."
              />
            ),
          }}
        />
      </div>

      <PurchaseDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        editingPurchase={editingPurchase}
        suppliers={suppliers}
        refetchSuppliers={refetchSuppliers}
      />

      <BulkBarcodePrintModal
        open={barcodePrintModalOpen}
        onClose={() => {
          setBarcodePrintModalOpen(false);
          setBarcodePrintItems([]);
        }}
        items={barcodePrintItems}
      />
    </div>
  );
};
